import React, { useCallback, useEffect, useMemo, useState } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import useAllAccounts from "@fartherfinance/frontend/api/Accounts/hooks/useAllAccounts";
import {
  GoalBasedFinancialPlanAccountResponse,
  PlatformAccountType,
} from "@fartherfinance/frontend/api/FinancialPlans/account/types";
import {
  AltAssetId,
  ClientId,
  ExternalAccountId,
  FartherAccountId,
} from "@fartherfinance/frontend/api/Types";

import { OwnerField } from "../Shared/constants";
import FormDrawer from "../Shared/FormDrawer/FormDrawer";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import FlexWrapper from "@src/sharedComponents/Forms/FlexWrapper";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import Spacer from "@src/sharedComponents/Forms/Spacer";
import RadioButtons from "@src/sharedComponents/Radio/RadioButtons";
import removeCommasUnd from "@src/utils/CommaHelperFunctions/removeCommasUnd";
import { getExternalAccountBalance } from "@src/utils/ExternalAccount/getExternalAccountBalance";
import {
  formNumberFieldWithCommasCustomRegex,
  formNumberFieldWithCommasRules,
  formPercentFieldAccept0CustomRegex,
} from "@src/utils/formValidation";

export interface ExtraAccountData {
  marketValue: string | undefined;
  returns: string | undefined;
  volatility: string | undefined;
}

const RadioButtonsValues = [
  "Low (3%)",
  "Moderate (10%)",
  "High (15%)",
  "Very high (25%)",
] as const;
type RadioButtonValue = typeof RadioButtonsValues[number];

const AccountField = "AccountField";
const MarketValueField = "MarketValueField";
const ReturnRateField = "ReturnRateField";

type Form = {
  [OwnerField]: string;
  [AccountField]: string;
  [MarketValueField]: string | undefined;
  [ReturnRateField]: string | undefined;
};

interface Props {
  drawerOpen: boolean;
  onClose: () => void;
  isMutating: boolean;
  formTitle: string;
  owner: string;
  accountType: PlatformAccountType | undefined;
  accountId: FartherAccountId | AltAssetId | ExternalAccountId | undefined;
  accountDisplayName: string;
  existingAccountData: GoalBasedFinancialPlanAccountResponse | undefined;
  onAddOrUpdateAccountData: (accountData: ExtraAccountData) => void;
}

const AssetsAndLiabilitiesForm: React.FC<Props> = ({
  drawerOpen,
  onClose,
  isMutating,
  formTitle,
  owner,
  accountType,
  accountId,
  accountDisplayName,
  existingAccountData,
  onAddOrUpdateAccountData,
}) => {
  const { clientId } = useParams<{
    clientId: ClientId;
  }>();

  const setDefaultVolatility = useCallback(() => {
    const existingVol = existingAccountData?.accountData?.data.volatility;
    if (existingVol) {
      if (existingVol === "3") {
        return RadioButtonsValues[0];
      } else if (existingVol === "10") {
        return RadioButtonsValues[1];
      } else if (existingVol === "15") {
        return RadioButtonsValues[2];
      } else if (existingVol === "25") {
        return RadioButtonsValues[3];
      }
    }

    return RadioButtonsValues[1];
  }, [existingAccountData?.accountData?.data.volatility]);

  const [radioButtonValue, setRadioButtonValue] = useState<RadioButtonValue>(
    setDefaultVolatility()
  );

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      [OwnerField]: owner,
      [AccountField]: accountDisplayName,
      [MarketValueField]: existingAccountData?.accountData?.data.marketValue,
      [ReturnRateField]: existingAccountData?.accountData?.data.returns,
    },
  });

  const {
    formState: { isDirty, isValid },
    getValues,
    reset,
  } = methods;

  const auth = useRequestAuth();
  const allAccounts = useAllAccounts(clientId, auth);

  const externalAccountBalance = useMemo(() => {
    if (
      accountType === "ExternalAccount" &&
      accountId !== undefined &&
      allAccounts.data !== undefined
    ) {
      const exAcct = allAccounts.data.externalAccounts.find(
        (a) => a.accountId === accountId
      );
      if (exAcct === undefined) {
        throw new Error(
          `External account: ${accountId} could not be found in list of external accounts`
        );
      }

      const balance = getExternalAccountBalance(exAcct);
      return balance !== undefined ? `${Math.floor(balance)}` : undefined; // remove cents, round down to nearest dollar
    }

    return undefined;
  }, [accountType, accountId, allAccounts.data]);

  const setFormFields = useCallback(() => {
    if (drawerOpen) {
      setRadioButtonValue(setDefaultVolatility());

      const existingValue = existingAccountData?.accountData?.data.marketValue;

      const accountValue =
        accountType === "ExternalAccount" && existingValue === undefined
          ? externalAccountBalance
          : existingValue;

      reset({
        [OwnerField]: owner,
        [AccountField]: accountDisplayName,
        [MarketValueField]: accountValue,
        [ReturnRateField]: existingAccountData?.accountData?.data.returns,
      });
    }
  }, [
    drawerOpen,
    reset,
    owner,
    accountDisplayName,
    existingAccountData,
    accountType,
    externalAccountBalance,
    setDefaultVolatility,
  ]);

  useEffect(() => {
    setFormFields();
  }, [setFormFields]);

  const onSave = () => {
    const values = getValues();

    let vol = "25";
    if (radioButtonValue === "Low (3%)") {
      vol = "3";
    } else if (radioButtonValue === "Moderate (10%)") {
      vol = "10";
    } else if (radioButtonValue === "High (15%)") {
      vol = "15";
    }

    onAddOrUpdateAccountData({
      marketValue:
        values[MarketValueField] === ""
          ? undefined
          : removeCommasUnd(values[MarketValueField]),
      returns:
        values[ReturnRateField] === "" ? undefined : values[ReturnRateField],
      volatility: vol,
    });
  };

  const handleOnClose = () => {
    setFormFields();
    onClose();
  };

  return (
    <FormDrawer
      title={formTitle}
      drawerOpen={drawerOpen}
      onClose={handleOnClose}
      saveDisabled={isMutating || !isValid || !isDirty}
      onSave={onSave}
      removeDisabled={true}
      onRemove={undefined}
      isLoading={isMutating}
      hasError={false}
      deleteModalTitle={""}
      deleteModalSubTitle={""}
    >
      <FormProvider {...methods}>
        <FlexWrapper>
          <FormTextField
            name={OwnerField}
            label="Owner"
            required={"Must not be empty"}
            disabled={true}
          />

          <FormTextField
            name={AccountField}
            label="Account"
            required={"Must not be empty"}
            disabled={true}
          />
        </FlexWrapper>

        <FlexWrapper>
          {accountType === "ExternalAccount" && (
            <FormNumberField
              name={MarketValueField}
              label={"Asset Value"}
              startAdornment={"$"}
              customRegex={formNumberFieldWithCommasCustomRegex}
              rules={formNumberFieldWithCommasRules({
                required: true,
                minValue: 0,
                maxValue: 1_000_000_000,
              })}
              autoAddCommas={true}
            />
          )}

          <FormNumberField
            name={ReturnRateField}
            label={"Expected Return Rate (annual)"}
            endAdornment={"%"}
            customRegex={formPercentFieldAccept0CustomRegex}
            rules={{
              min: {
                value: 0,
                message: `Can't be less than ${0}`,
              },
              max: {
                value: 999,
                message: `Can't be greater than ${999}`,
              },
            }}
          />
        </FlexWrapper>
      </FormProvider>

      <Spacer verticalSpacing="24px" />

      <RadioButtons
        label={"Expected volatility"}
        values={RadioButtonsValues}
        value={radioButtonValue}
        onChange={setRadioButtonValue}
        isValid={undefined}
      />
    </FormDrawer>
  );
};

export default AssetsAndLiabilitiesForm;
